import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddressCapture from '@components/AddressCapture/AddressCapture';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import { FormikSelectField } from '@shared/components/inputs/FormikSelectField';
import { FormikTextField } from '@shared/components/inputs/FormikTextField';
import type { ResolvedAddress } from '@t/address.types';
import {
  COUNTRY_OPTIONS,
  PROVINCE_OPTIONS,
  textFieldSx,
} from '@components/Checkout/steps/CustomerInfoStep/utils/customerInfoStepUtils';
import type { BillingAddressSectionProps } from '@components/Checkout/steps/CustomerInfoStep/CustomerInfoStep.types';

const BILLING_FIELDS = [
  'billingStreet',
  'billingUnit',
  'billingCity',
  'billingProvince',
  'billingPostalCode',
  'billingCountry',
] as const;

export const BillingAddressSection = ({ formik }: BillingAddressSectionProps) => {
  const { values, errors, touched, setFieldValue } = formik;

  const handleUseSameAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFieldValue('useSameAddress', checked);
    if (checked) {
      BILLING_FIELDS.forEach((field) => setFieldValue(field, ''));
    }
  };

  const handleAddressSelected = (address: ResolvedAddress) => {
    const street = [address.streetNumber, address.streetName].filter(Boolean).join(' ');
    setFieldValue('billingStreet', street);
    setFieldValue('billingUnit', address.unitNumber ?? '');
    setFieldValue('billingCity', address.city);
    setFieldValue('billingProvince', address.province);
    setFieldValue('billingPostalCode', address.postalCode);
    setFieldValue('billingCountry', address.country);
  };

  return (
    <>
      <Stack gap="10px">
        <Typography variant="heading.subsection" sx={{ lineHeight: '150%' }}>
          Billing Address
        </Typography>
        <Typography variant="body.md" sx={{ lineHeight: '150%' }}>
          This is the address we will use for billing. Make sure it matches the address on your payment information.
        </Typography>
      </Stack>
      <FormControlLabel
        control={<Checkbox checked={values.useSameAddress} onChange={handleUseSameAddressChange} />}
        label="Use the Service Address"
      />

      {!values.useSameAddress && (
        <>
          <Stack gap="10px">
            <TextFieldLabel label="Address Search" />
            <AddressCapture
              onAddressSelected={handleAddressSelected}
              defaultInputValue={values.billingStreet}
              placeholder="Starting typing and select your address..."
              error={touched.billingStreet && errors.billingStreet ? (errors.billingStreet as string) : undefined}
            />
          </Stack>

          <FormikSelectField
            formik={formik}
            name="billingCountry"
            label="Country"
            placeholder="Select Country"
            options={COUNTRY_OPTIONS}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} gap="15px" alignItems="flex-start">
            <FormikTextField
              formik={formik}
              name="billingUnit"
              label="Apartment, Unit, or Suite Number"
              optional
              placeholder="Enter unit number..."
              inputSx={textFieldSx}
              containerSx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
            />
            <FormikTextField
              formik={formik}
              name="billingStreet"
              label="Street Address"
              inputSx={textFieldSx}
              containerSx={{ width: { xs: '100%' }, flex: 1 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap="15px" alignItems="flex-start">
            <FormikTextField
              formik={formik}
              name="billingCity"
              label="City"
              inputSx={textFieldSx}
              containerSx={{ width: { xs: '100%' }, flex: 1 }}
            />
            <FormikSelectField
              formik={formik}
              name="billingProvince"
              label="Province/State"
              placeholder="Select Province/State"
              options={PROVINCE_OPTIONS}
              containerSx={{ width: { xs: '100%' }, flex: 1 }}
            />
            <FormikTextField
              formik={formik}
              name="billingPostalCode"
              label="Postal/Zip Code"
              inputSx={textFieldSx}
              containerSx={{ width: { xs: '100%', sm: '131px' }, flexShrink: 0 }}
            />
          </Stack>
        </>
      )}
    </>
  );
};
